import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '../../atoms/Avatar'
import { useCurrentUser } from '../../../hooks'
import { useAdvisorHome } from './AdvisorHomeContext'
import GroupScopeSelector from './GroupScopeSelector'
import ScopeDisplaySkeleton from './GroupScopeSelector/ScopeDisplaySkeleton'

const useStyles = makeStyles(() => ({
  scopeDisplay: ({ extraStyles }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '5px 0',
    gap: '1rem',
    ...(extraStyles || {})
  }),
  textColumn: ({ labelPosition }) => ({
    display: 'flex',
    flexDirection: labelPosition === 'bottom' ? 'column-reverse' : 'column',
    alignItems: 'flex-start'
  }),
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem'
  },
  label: ({ labelFontSize }) => ({
    fontSize: labelFontSize
  }),
  title: ({ titleFontSize }) => ({
    fontSize: titleFontSize,
    fontWeight: 'bold'
  }),
  dropdownIndicator: ({ labelFontSize }) => ({
    width: `calc(${labelFontSize} * 0.9)`,
    height: `calc(${labelFontSize} * 0.9)`,
    fill: 'currentColor',
    flexShrink: 0,
    transition: 'transform 150ms ease',
    // Caret rotates when the MUI menu is open. MUI v4 sets aria-controls
    // to "menu-list-grow" on the trigger button while the menu is visible.
    '[aria-controls="menu-list-grow"] &': {
      transform: 'rotate(180deg)'
    }
  })
}))

export const ScopeDisplayItem = ({
  name,
  label,
  avatarSrc,
  extraStyles,
  avatarSize = '5rem',
  avatarFontSize = '2rem',
  titleFontSize = '2rem',
  labelFontSize = '1.5rem',
  labelPosition = 'top'
}) => {
  const classes = useStyles({
    extraStyles,
    labelPosition,
    titleFontSize,
    labelFontSize
  })

  if (!name) return null

  return (
    <div className={classes.scopeDisplay}>
      <div>
        <Avatar
          customSize={avatarSize}
          customFontSize={avatarFontSize}
          avatarLetters={name}
          isPublicResource
          src={avatarSrc}
        />
      </div>
      <div className={classes.textColumn}>
        {label && (
          <div className={classes.labelRow}>
            <div className={classes.label}>{label}</div>
            <svg
              className={classes.dropdownIndicator}
              viewBox='0 0 24 24'
              focusable='false'
              aria-hidden='true'
            >
              <path d='M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z' />
            </svg>
          </div>
        )}
        <div className={classes.title}>{name}</div>
      </div>
    </div>
  )
}

ScopeDisplayItem.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  extraStyles: PropTypes.object,
  avatarSrc: PropTypes.string,
  avatarSize: PropTypes.string,
  avatarFontSize: PropTypes.string,
  titleFontSize: PropTypes.string,
  labelFontSize: PropTypes.string,
  labelPosition: PropTypes.oneOf(['top', 'bottom'])
}

const GroupScopeDisplay = ({
  scope,
  setScope,
  headingLabel,
  avatarSize,
  avatarFontSize,
  titleFontSize,
  labelFontSize,
  labelPosition
}) => {
  const onChangeGroupScope = useCallback((group) => {
    const scopeValues = group?.groupId ? [group.groupId] : []

    // setScope sets url state and such appropriately
    setScope({ values: scopeValues })
  }, [setScope])

  return (
    <GroupScopeSelector
      scope={scope}
      label={headingLabel}
      onChange={onChangeGroupScope}
      avatarSize={avatarSize}
      avatarFontSize={avatarFontSize}
      titleFontSize={titleFontSize}
      labelFontSize={labelFontSize}
      labelPosition={labelPosition}
    />
  )
}

GroupScopeDisplay.propTypes = {
  scope: PropTypes.object,
  setScope: PropTypes.func,
  headingLabel: PropTypes.string,
  avatarSize: PropTypes.string,
  avatarFontSize: PropTypes.string,
  titleFontSize: PropTypes.string,
  labelFontSize: PropTypes.string,
  labelPosition: PropTypes.oneOf(['top', 'bottom'])
}

const UserScopeDisplay = ({
  headingLabel,
  avatarSize,
  avatarFontSize,
  titleFontSize,
  labelFontSize,
  labelPosition
}) => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser()

  if (isLoadingUser) {
    return <ScopeDisplaySkeleton />
  }

  return (
    <ScopeDisplayItem
      name={user?.fullName}
      avatarSrc={user?.profilePictureUrl}
      label={headingLabel}
      avatarSize={avatarSize}
      avatarFontSize={avatarFontSize}
      titleFontSize={titleFontSize}
      labelFontSize={labelFontSize}
      labelPosition={labelPosition}
    />
  )
}

UserScopeDisplay.propTypes = {
  headingLabel: PropTypes.string,
  avatarSize: PropTypes.string,
  avatarFontSize: PropTypes.string,
  titleFontSize: PropTypes.string,
  labelFontSize: PropTypes.string,
  labelPosition: PropTypes.oneOf(['top', 'bottom'])
}

const ScopeDisplay = ({
  headingLabel,
  avatarSize,
  avatarFontSize,
  titleFontSize,
  labelFontSize,
  labelPosition
}) => {
  const { scope, setScope } = useAdvisorHome()

  const displayProps = {
    avatarSize,
    avatarFontSize,
    titleFontSize,
    labelFontSize,
    labelPosition
  }

  if (!scope) {
    return <UserScopeDisplay headingLabel={headingLabel} {...displayProps} />
  }

  return (
    <GroupScopeDisplay
      scope={scope}
      setScope={setScope}
      headingLabel={headingLabel}
      {...displayProps}
    />
  )
}

ScopeDisplay.propTypes = {
  headingLabel: PropTypes.string,
  avatarSize: PropTypes.string,
  avatarFontSize: PropTypes.string,
  titleFontSize: PropTypes.string,
  labelFontSize: PropTypes.string,
  labelPosition: PropTypes.oneOf(['top', 'bottom'])
}

export default ScopeDisplay
